import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { useTheme } from '@mui/material';
import clsx from 'clsx';
import _ from 'lodash';
import PropTypes from 'prop-types';

import useDevicePixelRatio from 'hooks/useDevicePixelRatio';
import useVisibility from 'hooks/useVisibility';
import { logger } from 'utils/logger';

import { showMessageAction } from 'store/global/actions';

import {
  CANVAS_CONTEXT_ATTRIBUTES,
  FULL_HEIGHT,
  FULL_WIDTH,
  PDF_CARD_ERROR_MESSAGE,
  THUMBNAIL_HEIGHT,
  THUMBNAIL_WIDTH,
} from './PdfCard.constants';
import { getPdfCardStyles } from './PdfCard.styles';
import {
  applyCanvasCssSize,
  checkIsCanvasPaintCurrent,
  checkIsRenderErrorSilent,
  getCanvasCssBox,
  getCanvasCssSize,
  getCanvasRenderTarget,
  getPageViewportSize,
  paintCanvasBackground,
  queueOffscreenPaint,
  releaseCanvasBackingStore,
  releaseCanvasToPaper,
  resizeCanvasBackingStore,
} from './PdfCard.utils';

const PdfCard = ({
  num = 1,
  scale = 1,
  rotation = 0,
  pdf = null,
  pageSize = null,
  isSelected = false,
  isThumbnail = false,
  isOffscreenReleaseEnabled = false,
  isInRenderWindow = false,
  canvasRef = null,
  ...props
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const devicePixelRatio = useDevicePixelRatio();

  const canvas = useRef(null);
  const renderTaskRef = useRef(null);
  const paintedKeyRef = useRef(null);
  const queuedPaintRef = useRef(null);
  // Thumbnails track visibility continuously so a density change repaints only the on-screen ones.
  const isVisible = useVisibility(canvas, null, {
    once: !isOffscreenReleaseEnabled && !isThumbnail,
  });
  const isVisibleRef = useRef(isVisible);
  const isPaintAllowed = isVisible || (isOffscreenReleaseEnabled && isInRenderWindow);

  const cssSize = useMemo(() => {
    if (!pageSize) {
      return null;
    }

    return getCanvasCssSize(
      getPageViewportSize({ pageSize, scale, rotation: isThumbnail ? 0 : rotation }),
    );
  }, [pageSize, scale, rotation, isThumbnail]);
  const cssBox = cssSize ? getCanvasCssBox(cssSize) : null;
  const styles = getPdfCardStyles({ theme, isSelected, isThumbnail, cssBox });
  const paperColor = theme.palette.common.white;

  const placeholderWidth = isThumbnail ? THUMBNAIL_WIDTH : FULL_WIDTH;
  const placeholderHeight = isThumbnail ? THUMBNAIL_HEIGHT : FULL_HEIGHT;

  useEffect(() => {
    if (!canvasRef) {
      return;
    }

    canvasRef.current = canvas.current;
  }, [canvasRef]);

  // Read by the render effect without re-running it: a retained page scrolling in or out keeps its
  // in-flight render. A queued paint starts at once when its page scrolls into view, so a jump to a
  // page does not wait behind the offscreen pages above it.
  useEffect(() => {
    isVisibleRef.current = isVisible;

    if (isVisible) {
      queuedPaintRef.current?.().catch(_.noop);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!pdf) {
      return;
    }

    const renderRun = { isStale: false };

    const renderPage = async () => {
      try {
        const page = await pdf.getPage(num);

        if (renderRun.isStale || !canvas.current) {
          return;
        }

        const viewport = page.getViewport({
          scale,
          rotation: isThumbnail ? page.rotate : page.rotate + rotation,
        });

        if (!cssSize) {
          applyCanvasCssSize(canvas.current, viewport);
        }

        if (!isPaintAllowed) {
          if (isOffscreenReleaseEnabled) {
            releaseCanvasToPaper({ canvas: canvas.current, color: paperColor });
            page.cleanup();
          }

          return;
        }

        const { backingSize, transform } = getCanvasRenderTarget({
          cssSize: cssSize ?? getCanvasCssSize(viewport),
          isThumbnail,
          devicePixelRatio,
        });
        const paintKey = {
          pdf,
          num,
          scale: viewport.scale,
          rotation: viewport.rotation,
          backingSize,
          transform,
          paperColor,
        };

        if (
          checkIsCanvasPaintCurrent({
            canvas: canvas.current,
            paintedKey: paintedKeyRef.current,
            paintKey,
          })
        ) {
          return;
        }

        const paint = async () => {
          if (renderRun.isStale || !canvas.current) {
            return;
          }

          // Cleared before the canvas is touched, so a paint cancelled mid-way never reads as current.
          paintedKeyRef.current = null;
          resizeCanvasBackingStore(canvas.current, backingSize);

          const context = canvas.current.getContext('2d', CANVAS_CONTEXT_ATTRIBUTES);

          paintCanvasBackground({ canvas: canvas.current, context, color: paperColor });

          const renderTask = page.render({
            canvas: null,
            canvasContext: context,
            viewport,
            transform,
          });

          renderTaskRef.current = renderTask;

          try {
            await renderTask.promise;
            paintedKeyRef.current = paintKey;
          } finally {
            // cancel() on a settled pdf.js task is not a no-op: it re-runs endDrawing and drops
            // the canvas lock even when a newer task holds it.
            if (renderTaskRef.current === renderTask) {
              renderTaskRef.current = null;
            }
          }
        };

        if (isVisibleRef.current) {
          await paint();

          return;
        }

        const paintOnce = _.once(paint);

        queuedPaintRef.current = paintOnce;
        await queueOffscreenPaint(paintOnce);
      } catch (err) {
        if (checkIsRenderErrorSilent({ error: err, isStale: renderRun.isStale, pdf })) {
          return;
        }

        dispatch(showMessageAction({ message: PDF_CARD_ERROR_MESSAGE, variant: 'error' }));
        logger.error(err);
      }
    };

    renderPage();

    return () => {
      renderRun.isStale = true;
      queuedPaintRef.current = null;
      renderTaskRef.current?.cancel();
    };
  }, [
    pdf,
    num,
    scale,
    rotation,
    isThumbnail,
    isPaintAllowed,
    isOffscreenReleaseEnabled,
    cssSize,
    devicePixelRatio,
    paperColor,
    dispatch,
  ]);

  useEffect(() => {
    const canvasElement = canvas.current;

    return () => {
      paintedKeyRef.current = null;

      if (canvasElement && !canvasElement.isConnected) {
        releaseCanvasBackingStore(canvasElement);
      }
    };
  }, [pdf]);

  if (!pdf) {
    return null;
  }

  return (
    <canvas
      className={clsx(isThumbnail && 'tool__disable-interaction')}
      data-testid={props['data-testid']}
      height={isOffscreenReleaseEnabled ? 0 : placeholderHeight}
      ref={canvas}
      style={styles}
      width={isOffscreenReleaseEnabled ? 0 : placeholderWidth}
    />
  );
};

PdfCard.propTypes = {
  canvasRef: PropTypes.shape({ current: PropTypes.object }),
  'data-testid': PropTypes.string,
  num: PropTypes.number,
  scale: PropTypes.number,
  rotation: PropTypes.number,
  pdf: PropTypes.object,
  pageSize: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
  isSelected: PropTypes.bool,
  isThumbnail: PropTypes.bool,
  isOffscreenReleaseEnabled: PropTypes.bool,
  isInRenderWindow: PropTypes.bool,
};

export default memo(PdfCard);
